import React, { createContext, useContext, useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { IS_DUAL_MODE, MANAGEMENT_ENABLED } from "./site-mode";

/**
 * Tracks which product flow the visitor is in:
 *  - "published"  → Published Projects (Solutions, published pricing)
 *  - "management" → Project Management (Features, PM pricing)
 *
 * The flow is remembered in localStorage so shared pages
 * (Pricing, About, Search, Contact) keep the visitor's context.
 * Product-specific routes switch the flow automatically.
 */

export const PRODUCT_PUBLISHED = "published";
export const PRODUCT_MANAGEMENT = "management";

const STORAGE_KEY = "conra-product-flow";

const PUBLISHED_ROUTES = [/^\/$/, /^\/solutions(\/|$)/];
const MANAGEMENT_ROUTES = [/^\/products\/project-management(\/|$)/, /^\/platform\/features(\/|$)/];

const readStored = () => {
    try {
        const v = window.localStorage.getItem(STORAGE_KEY);
        return v === PRODUCT_MANAGEMENT ? PRODUCT_MANAGEMENT : PRODUCT_PUBLISHED;
    } catch {
        return PRODUCT_PUBLISHED;
    }
};

const ProductFlowContext = createContext({
    product: PRODUCT_PUBLISHED,
    setProduct: () => { },
});

/** In single-mode builds the flow is a constant — no switching, no storage. */
const FIXED_PRODUCT = IS_DUAL_MODE
    ? null
    : (MANAGEMENT_ENABLED ? PRODUCT_MANAGEMENT : PRODUCT_PUBLISHED);

export const ProductFlowProvider = ({ children }) => {
    const [product, setProductState] = useState(() => FIXED_PRODUCT || readStored());
    const location = useLocation();

    const setProduct = useCallback((p) => {
        if (FIXED_PRODUCT) return; // single-mode build: flow can't change
        try {
            window.localStorage.setItem(STORAGE_KEY, p);
        } catch {
            /* storage unavailable — keep in-memory only */
        }
        setProductState(p);
    }, []);

    // Product-specific routes switch the flow automatically (dual mode only).
    useEffect(() => {
        if (FIXED_PRODUCT) return;
        if (PUBLISHED_ROUTES.some((r) => r.test(location.pathname))) {
            setProduct(PRODUCT_PUBLISHED);
        } else if (MANAGEMENT_ROUTES.some((r) => r.test(location.pathname))) {
            setProduct(PRODUCT_MANAGEMENT);
        }
    }, [location.pathname, setProduct]);

    return (
        <ProductFlowContext.Provider value={{ product, setProduct }}>
            {children}
        </ProductFlowContext.Provider>
    );
};

export const useProductFlow = () => useContext(ProductFlowContext);
